import { useState } from "react"
import { cn } from "@/lib/cn"
import { toneCss } from "../format"
import type { ActionPayload, ActionRowBlock } from "../types"

type Props = {
  block: ActionRowBlock
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<unknown>
}

// Multiple action buttons in a row. Stacks full-width at xs/sm via the
// container-query stylesheet; inline-wraps at md+.
export function ActionRowBlockView({ block, onAction }: Props) {
  const actions = Array.isArray(block.actions) ? block.actions : []
  return (
    <div className="flex flex-wrap gap-2 valet-uidoc-actionrow">
      {actions.map((a, idx) => (
        <ActionRowItem key={`${a.label}-${idx}`} action={a} onAction={onAction} />
      ))}
    </div>
  )
}

function ActionRowItem({
  action, onAction,
}: {
  action: ActionPayload
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<unknown>
}) {
  const [busy, setBusy] = useState(false)
  const css = toneCss(action.tone)

  async function click() {
    if (busy) return
    if (action.confirm && !window.confirm(action.confirm)) return
    setBusy(true)
    try {
      await onAction(action)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      className={cn(
        "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-control border px-4 py-2 text-sm font-semibold transition",
        css.text,
        css.bg,
        css.border,
        "hover:brightness-110 disabled:cursor-wait disabled:opacity-60",
      )}
    >
      {busy ? "Working…" : action.label}
    </button>
  )
}
